// Построение MapLibre text-field выражений для локализованных подписей -
// вынесены из MapView.vue (см. пояснение в constants.ts этой же папки).
//
// buildPeakLabelField/buildPassLabelField в исходном компоненте замыкались
// на `t` из useI18n() (для суффикса "м" единиц высоты) - здесь это явный
// параметр, обновлены и вызывающие места в MapView.vue/basemapLayers.ts.
// buildNameField `t` не использует, поэтому его сигнатура не изменилась.
//
// applyLabelLocale/labelLang/MAP_LABEL_LANGS/MAP_LABEL_FALLBACK_LANG/
// LOCALIZED_LABEL_FIELDS остаются в MapView.vue - они читают мутируемые
// `map`/`mapReady` компонента напрямую.

type Translate = (key: string) => string;

// Выражение MapLibre - статичные данные, вычисляемые на объект при рендере,
// язык не может быть переменной ВНУТРИ него - `['get', 'name:' + x]`
// ничего не значит для вычислителя выражений. Локаль подставляется здесь, на
// этапе сборки массива, а весь массив целиком меняется через setLayoutProperty.
//
// Покрытие тегов `name:<lang>` неравномерное - плотное вокруг Алматы, редкое
// на диких тропах, поэтому это цепочка, а не простой lookup: выбранный язык,
// затем английский (латиница лучше пустоты для казахоязычного объекта при
// английском интерфейсе), затем обычный `name`, и наконец пустая строка -
// это важно: `coalesce`, вернувший null, оставил бы text-field null, а ""
// вообще не ставит бокс подписи, что и нужно безымянному объекту.
export const buildNameField = (lang: string): any => {
  const keys = lang === "en" ? ["name_en"] : [`name_${lang}`, "name_en"];
  return ["coalesce", ...keys.map((key) => ["get", key]), ["get", "name"], ""];
};

// Вершины несут имя и высоту в одном символе, поэтому их text-field -
// составной, обычная цепочка тут не годится. Обе половины следуют локали:
// имя из тайла, суффикс метров - из строк приложения, чтобы жёстко зашитое
// кириллическое «м» не осталось под английским названием вершины.
export const buildPeakLabelField = (t: Translate, lang: string): any => {
  const name = buildNameField(lang);
  const ele = ["concat", ["to-string", ["round", ["get", "ele"]]], ` ${t("units.m")}`];
  return [
    "step",
    ["zoom"],
    "",
    12,
    [
      "case",
      ["all", ["has", "name"], ["has", "ele"]],
      ["concat", name, "\n", ele],
      ["has", "name"],
      name,
      ["has", "ele"],
      ele,
      "",
    ],
  ];
};

// Перевалы получают тот же стек имя+высота, что и вершины, плюс то, чего нет
// у вершин - категорию сложности. `difficulty` - тег `rtsa_scale`,
// советская/российская альпинистская шкала (н/к, 1А…3Б), и это не украшение -
// "Талгарский провал 3А*" говорит, что перевалу нужны верёвка и напарник,
// тогда как голое имя не говорит ничего. Тег есть у 3331 из 3956 перевалов в
// этом экстракте, и 360 перевалов внутри MAP_MAX_BOUNDS - здесь это хорошо
// заполненный тег, а не редкость, какой выглядит в остальном мире.
//
// Категория стоит на строке имени, а не отдельно, потому что читается как
// часть идентичности перевала - так альпинисты произносят его вслух.
// Безымянные перевалы падают до одной высоты; категория без имени была бы
// плавающей "1Б" над голой точкой.
export const buildPassLabelField = (t: Translate, lang: string): any => {
  const name = buildNameField(lang);
  const graded = [
    "case",
    ["has", "difficulty"],
    ["concat", name, " ", ["get", "difficulty"]],
    name,
  ];
  const ele = ["concat", ["to-string", ["round", ["get", "ele"]]], ` ${t("units.m")}`];
  return [
    "step",
    ["zoom"],
    "",
    12,
    [
      "case",
      ["all", ["has", "name"], ["has", "ele"]],
      ["concat", graded, "\n", ele],
      ["has", "name"],
      graded,
      ["has", "ele"],
      ele,
      "",
    ],
  ];
};
